// TODO: add prop types and default props and remove this linter bypass
/* eslint react/prop-types: 0 */
import React, { useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, message, Select, Switch } from 'antd';

import { borderGrey, searchHeaderBorderGrey } from 'common/constants/colors';
import {
  makeApiRequestAndHandleError,
  simpleFetch,
  simplePost,
  simplePut,
} from 'common/utils/http';
import { VALID_DATATYPES } from 'common/constants/datatypes';
import PaneHeader from './PaneHeader';
import { AttributeType, Section } from '../../../common/constants/types';
import AccessRuleGroupSelect from './AccessRuleGroupSelect';

const LISTABLE_HELP =
  'Listable is true if attributes of this type have a low cardinality. ' +
  '(E.g. "email" is not listable since everyone has their own; "courses" is listable since ' +
  'there will never be very many.)';
const SINGLETON_HELP =
  'If Singleton is true then we will not allow a user to have more' +
  'than one of this attribute type';
const ALLOW_PASSTHROUGH_MODIFY_HELP =
  'If enabled, derived attributes of this type can be modified/deleted even if the user ' +
  'lacks the CanModify permission (the permission check is bypassed for derived attributes only).';


type AttributeTypeWithId = AttributeType & { id: number };

const prepareFormData = async (formData: { [field: string]: string }) => {
  const section = await simpleFetch(`/api/v1/sections/${formData.section_name}`);
  return { ...formData, section_id: section.id };
};

interface NewAttributeTypeFormProps {
  section: Section;
  onSubmit: () => void;
  onCancel: () => void;
  editAttrType: AttributeType | null;
}
const NewAttributeTypeForm = ({ section, onSubmit, onCancel, editAttrType }: NewAttributeTypeFormProps) => {
  const [info, setInfo] = useState<AttributeTypeWithId>({} as AttributeTypeWithId);
  const [form] = Form.useForm();

  useEffect(() => {
    if (editAttrType) {
      makeApiRequestAndHandleError(async () => {
        const at = await simpleFetch(`/api/v1/attribute-types/${editAttrType.name}`);
        setInfo(at);
        form.setFieldsValue({
          name: at.name,
          datatype: at.datatype,
          description: at.description,
          sort_order: at.sort_order,
          section_name: section.name,
          can_modify_access_rule_group: at.can_modify_access_rule_group,
          can_view_access_rule_group: at.can_view_access_rule_group,
          listable: at.listable,
          singleton: at.singleton,
          allow_passthrough_modify_access_rule_group: at.allow_passthrough_modify_access_rule_group,
        });
      });
    } else {
      // Reset form when adding a new attribute type
      form.resetFields();
    }
  }, [editAttrType, form, section.name]);

  const handleSubmit = async () => {
    const formData = await prepareFormData(form.getFieldsValue());
    try {
      if (editAttrType) {
        simplePut(`/api/v1/attribute-types/${info.id}`, { ...formData, id: info.id });
      } else {
        simplePost('/api/v1/attribute-types', formData);
      }
    } catch {
      message.error('Something went wrong. Please try again later');
      return;
    }
    message.success('Action Success!');
    onSubmit();
  };

  return (
    <Form colon={false} form={form} onFinish={handleSubmit}>
      <div style={{ margin: '10px', height: '70vh', overflow: 'scroll' }}>
        <Form.Item
          initialValue={info.name}
          label="Name"
          name="name"
        >
          <Input />
        </Form.Item>
        <Form.Item
          initialValue={info.datatype}
          label="Datatype"
          name="datatype"
        >
          <Select getPopupContainer={e => e}>
            {VALID_DATATYPES.map(type => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          initialValue={info.description}
          label="Description"
          name="description"
        >
          <Input />
        </Form.Item>
        <Form.Item
          initialValue={info.sort_order}
          label="Sort Order"
          name="sort_order"
        >
          <InputNumber />
        </Form.Item>
        <Form.Item
          initialValue={section.name}
          label="Belongs to Section"
          name="section_name"
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          initialValue={info.can_modify_access_rule_group}
          label="Can Modify Access Rule Group"
          name="can_modify_access_rule_group"
        >
          <AccessRuleGroupSelect />
        </Form.Item>
        <Form.Item
          initialValue={info.can_view_access_rule_group}
          label="Can View Access Rule Group"
          name="can_view_access_rule_group"
        >
          <AccessRuleGroupSelect />
        </Form.Item>
        <Form.Item
          extra={LISTABLE_HELP}
          initialValue={info.listable}
          label="Listable"
          labelAlign="left"
          labelCol={{ span: 18 }}
          name="listable"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          extra={SINGLETON_HELP}
          initialValue={info.singleton}
          label="Singleton"
          labelAlign="left"
          labelCol={{ span: 18 }}
          name="singleton"
          valuePropName='checked'
        >
          <Switch />
        </Form.Item>
        <Form.Item
          extra={ALLOW_PASSTHROUGH_MODIFY_HELP}
          initialValue={info.allow_passthrough_modify_access_rule_group}
          label="Allow Passthrough Modify Access Rule Group"
          labelAlign="left"
          labelCol={{ span: 18 }}
          name="allow_passthrough_modify_access_rule_group"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </div>
      <Form.Item>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            borderTop: `1px solid ${searchHeaderBorderGrey}`,
            paddingTop: '20px',
          }}
        >
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            htmlType="submit"
            onSubmit={onSubmit}
            style={{ marginLeft: '20px' }}
            type="primary"
          >
            Submit
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

const AddAttributeTypePane = ({ editAttrType, onCancel, onSubmit, section }: NewAttributeTypeFormProps) => {
  const paneStyle = {
    borderRight: `solid 1px ${borderGrey}`,
    height: '100%',
    width: '25vw',
  };

  return (
    <div style={paneStyle}>
      <PaneHeader paneName={`${editAttrType ? 'Edit' : 'Add'} Attribute Type`} />
      <NewAttributeTypeForm
        editAttrType={editAttrType}
        onCancel={onCancel}
        onSubmit={onSubmit}
        section={section}
      />
    </div>
  );
};

export default AddAttributeTypePane;
